<template>
  <div class="mde-root">
    <!-- Toolbar -->
    <div class="mde-toolbar">
      <div class="mde-tb-group">
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.bold')" @click="wrap('**','**')"><b>B</b></button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.italic')" @click="wrap('*','*')"><i>I</i></button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.strikethrough')" @click="wrap('~~','~~')"><del>S</del></button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.inlineCode')" @click="wrap('`','`')">&lt;/&gt;</button>
      </div>
      <span class="mde-tb-sep" />
      <div class="mde-tb-group">
        <button class="mde-tb-btn" title="H1" @click="prefix('# ')">H1</button>
        <button class="mde-tb-btn" title="H2" @click="prefix('## ')">H2</button>
        <button class="mde-tb-btn" title="H3" @click="prefix('### ')">H3</button>
      </div>
      <span class="mde-tb-sep" />
      <div class="mde-tb-group">
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.ul')" @click="prefix('- ')">&#8226;</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.ol')" @click="prefix('1. ')">1.</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.task')" @click="prefix('- [ ] ')">&#9744;</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.quote')" @click="prefix('> ')">&ldquo;</button>
      </div>
      <span class="mde-tb-sep" />
      <div class="mde-tb-group">
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.link')" @click="wrap('[','](url)')">&#128279;</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.image')" @click="wrap('![alt](',')')">&#128247;</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.table')" @click="insert('table')">&#9776;</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.code')" @click="insert('code')">```</button>
        <button class="mde-tb-btn" :title="$t('tool.MarkdownEditor.toolbar.hr')" @click="insert('hr')">&mdash;</button>
      </div>
      <div class="mde-tb-right">
        <span class="mde-stats" v-if="statsText">{{ statsText }}</span>
        <button class="mde-tb-btn mde-tb-sample" :title="$t('tool.MarkdownEditor.loadSample')" @click="loadSample">{{ $t("common.action.sample") }}</button>
      </div>
    </div>

    <!-- Panel: editor + preview as one continuous card -->
    <div class="mde-panel">
      <div class="mde-section">
        <div class="mde-section-hd">{{ $t('tool.MarkdownEditor.input') }}</div>
        <textarea
          ref="ta" v-model="text"
          :placeholder="$t('tool.MarkdownEditor.phInput')"
          class="mde-ta"
          @keydown.tab.prevent="insertTab"
          @scroll="syncScroll"
        />
      </div>
      <div class="mde-section-divider" />
      <div class="mde-section" v-if="text.trim()">
        <div class="mde-section-hd">{{ $t('tool.MarkdownEditor.preview') }}</div>
        <div class="mde-preview mde-c" v-html="html" />
      </div>
      <div class="mde-section mde-section-empty" v-else>
        <div class="mde-empty-icon">&#9998;</div>
        <div class="mde-empty-title">{{ $t('tool.MarkdownEditor.previewTitle') }}</div>
        <div class="mde-empty-text">{{ $t('tool.MarkdownEditor.previewHint') }}</div>
        <div class="mde-empty-hint">
          <div class="mde-quick-ref">{{ $t('tool.MarkdownEditor.quickRef') }}</div>
          <div v-for="tip in tips" :key="tip.key">{{ tip.key }} &mdash; {{ tip.desc }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
var SAMPLE = '# Markdown 编辑器\n\n**粗体** *斜体* ~~删除线~~ `行内代码`\n\n## 代码块\n\n```javascript\nfunction hello() {\n  console.log("Hello World")\n}\n```\n\n## 列表\n\n- 项目一\n- 项目二\n  - 子项目\n- 项目三\n\n### 有序列表\n\n1. 第一步\n2. 第二步\n3. 第三步\n\n### 任务列表\n\n- [x] 已完成\n- [ ] 待办\n\n## 引用\n\n> 这是一段引用\n\n## 链接 & 图片\n\n[示例链接](https://example.com)\n\n![图片](https://picsum.photos/400/200)\n\n## 表格\n\n| 名称 | 描述 |\n|------|------|\n| JSON | 数据交换格式 |\n| YAML | 配置语言 |\n\n---\n\n试试顶部**工具栏**按钮快速插入格式'

function render(md) {
  if (!md) return ''
  var h = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  // code blocks
  h = h.replace(/```(\w*)\n([\s\S]*?)```/g, function(_, lang, code) {
    return '<pre><code>' + code.replace(/\n$/, '') + '</code></pre>'
  })
  // inline code
  h = h.replace(/`([^`\n]+)`/g, '<code>$1</code>')

  // headings
  h = h.replace(/^#### (.+)$/gm, '<h4>$1</h4>')
  h = h.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  h = h.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  h = h.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  // bold/italic
  h = h.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  h = h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  h = h.replace(/\*(.+?)\*/g, '<em>$1</em>')
  h = h.replace(/~~(.+?)~~/g, '<del>$1</del>')

  // links & images
  h = h.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" loading="lazy">')
  h = h.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')

  // hr
  h = h.replace(/^(---|\*\*\*|___)\s*$/gm, '<hr>')

  // blockquote
  h = h.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>')

  // ordered list
  var lines = h.split('\n'), out = [], inOL = false
  for (var i = 0; i < lines.length; i++) {
    var olMatch = lines[i].match(/^(\d+)\.\s+(.+)/)
    if (olMatch) {
      if (!inOL) { out.push('<ol>'); inOL = true }
      out.push('<li>' + olMatch[2] + '</li>')
    } else {
      if (inOL) { out.push('</ol>'); inOL = false }
      out.push(lines[i])
    }
  }
  if (inOL) out.push('</ol>')
  h = out.join('\n')

  // tables
  var lines2 = h.split('\n'), out2 = [], inTable = false, tableRows = [], headerIdx = -1
  function flushTable() {
    if (!tableRows.length) return
    out2.push('<table>')
    for (var r = 0; r < tableRows.length; r++) {
      var cells = tableRows[r].slice(1, -1).split('|')
      var tag = r === headerIdx ? 'th' : 'td'
      out2.push('<tr>' + cells.map(function(c) { return '<' + tag + '>' + c.trim() + '</' + tag + '>' }).join('') + '</tr>')
    }
    out2.push('</table>')
    tableRows = []; headerIdx = -1
  }
  for (var j = 0; j < lines2.length; j++) {
    var l2 = lines2[j]
    if (/^\|.+\|$/.test(l2)) {
      if (/^[\|\-:\s]+$/.test(l2.replace(/\|/g, ''))) {
        headerIdx = tableRows.length - 1
        continue
      }
      if (!inTable) { inTable = true }
      tableRows.push(l2)
    } else {
      if (inTable) { flushTable(); inTable = false }
      out2.push(l2)
    }
  }
  if (inTable) flushTable()
  h = out2.join('\n')

  // unordered list + task list — handle nesting by indent level
  var lines3 = h.split('\n'), out3 = [], stack = [] // { indent, tag, liOpen }
  function popTo(newIndent) {
    while (stack.length > 0 && stack[stack.length-1].indent > newIndent) {
      var top = stack.pop()
      if (top.liOpen) out3.push('</li>')
      out3.push('</ul>')
    }
    if (stack.length > 0 && stack[stack.length-1].indent === newIndent && stack[stack.length-1].liOpen) {
      out3.push('</li>')
      stack[stack.length-1].liOpen = false
    }
  }
  for (var k = 0; k < lines3.length; k++) {
    var l3 = lines3[k]
    var taskMatch = l3.match(/^(\s*)- \[([ x])\] (.+)/)
    var ulMatch = l3.match(/^(\s*)[-*]\s+(.+)/)
    if (taskMatch) {
      var tIndent = Math.floor(taskMatch[1].length / 2)
      popTo(tIndent)
      if (stack.length === 0 || stack[stack.length-1].indent < tIndent) {
        out3.push('<ul class="tasks">')
        stack.push({ indent:tIndent, tag:'tasks', liOpen:false })
      }
      var checked = taskMatch[2] === 'x' ? ' checked' : ''
      out3.push('<li class="task' + checked + '"><input type="checkbox"' + checked + ' disabled><span>' + taskMatch[3] + '</span>')
      stack[stack.length-1].liOpen = true
    } else if (ulMatch) {
      var indent = Math.floor(ulMatch[1].length / 2)
      popTo(indent)
      if (stack.length === 0 || stack[stack.length-1].indent < indent) {
        out3.push('<ul>')
        stack.push({ indent:indent, tag:'ul', liOpen:false })
      }
      out3.push('<li>' + ulMatch[2])
      stack[stack.length-1].liOpen = true
    } else {
      popTo(0)
      out3.push(l3)
    }
  }
  popTo(-1)
  h = out3.join('\n')

  // paragraphs for remaining plain text lines
  h = h.replace(/^(?!<[a-z/!])(.+)$/gm, function(m) { return m.trim() ? '<p>' + m + '</p>' : m })

  return h
}

export default {
  name:'MarkdownEditor',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return {
      text:'',
    }
  },
  computed:{
    html:function(){ return render(this.text) },
    tips:function(){ var t=this;var tk=t.$t('tool.MarkdownEditor.tips');return[
      { key:'**bold**', desc:tk.bold },
      { key:'*italic*', desc:tk.italic },
      { key:'`code`', desc:tk.code },
      { key:'[link](url)', desc:tk.link },
      { key:'- [ ] task', desc:tk.task },
      { key:'> quote', desc:tk.quote },
      { key:'| table |', desc:tk.table }
    ]},
    statsText:function(){
      var t = this.text
      if (!t.trim()) return ''
      return this.$t('tool.MarkdownEditor.stats', { chars: t.length, lines: t.split('\n').length })
    }
  },
  methods:{
    insertTab:function(){ this.wrap('\t','') },
    wrap:function(left, right){
      var ta = this.$refs.ta, v = ta.value, s = ta.selectionStart, e = ta.selectionEnd
      var sel = v.substring(s, e)
      this.text = v.substring(0, s) + left + sel + right + v.substring(e)
      this.$nextTick(function(){
        var ns = s + left.length, ne = e + left.length
        ta.focus(); ta.setSelectionRange(sel ? ns : ne, ne)
      })
    },
    prefix:function(p){
      var ta = this.$refs.ta
      var lineStart = ta.value.lastIndexOf('\n', ta.selectionStart - 1) + 1
      var before = ta.value.substring(0, lineStart), after = ta.value.substring(lineStart)
      this.text = before + p + after
      this.$nextTick(function(){
        ta.focus(); ta.setSelectionRange(lineStart + p.length, lineStart + p.length + after.length - 1)
      })
    },
    insert:function(type){
      if (type === 'code') this.wrap('```\n', '\n```')
      if (type === 'table') this.text += '\n| A | B | C |\n|---|---|---|\n| x | y | z |\n'
      if (type === 'hr') this.text += '\n---\n'
    },
    syncScroll:function(){
      var ta = this.$refs.ta, prevPane = this.$refs.prevPane
      if (!ta || !prevPane) return
      var max = ta.scrollHeight - ta.clientHeight
      if (max <= 0) return
      var ratio = ta.scrollTop / max
      var prev = prevPane.querySelector('.mde-preview')
      if (prev && prev.scrollHeight > prev.clientHeight) {
        prev.scrollTop = ratio * (prev.scrollHeight - prev.clientHeight)
      }
    },
    loadSample:function(){
      this.text = SAMPLE
      this.$nextTick(function(){ this.$refs.ta && this.$refs.ta.focus() })
    },
    doAction:function(a){
      if (a === 'clear') this.text = ''
      if (a === 'sample') this.loadSample()
      if (a === 'copy'){
        var el = this.$el.querySelector('.mde-c')
        if (el){
          var sel = window.getSelection(), range = document.createRange()
          sel.removeAllRanges(); range.selectNodeContents(el); sel.addRange(range)
          document.execCommand('copy'); sel.removeAllRanges()
          this.$message.success(this.$t('tool.MarkdownEditor.copied'))
        }
      }
    }
  }
}
</script>

<style>
.mde-root { width: 100%; display: flex; flex-direction: column; }

/* toolbar */
.mde-toolbar {
  display: flex; align-items: center; gap: 2px; padding: 6px 10px;
  background: #fff; border: 1px solid #dcdfe6; border-radius: 8px 8px 0 0; border-bottom: none;
  flex-wrap: wrap;
}
.mde-tb-group { display: flex; gap: 1px; }
.mde-tb-sep { width: 1px; height: 18px; background: #e4e7ed; margin: 0 6px; }
.mde-tb-btn {
  width: 30px; height: 30px; border: none; background: none; border-radius: 4px;
  font-size: 13px; color: #606266; cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all .12s;
}
.mde-tb-btn:hover { background: #f0f2f5; color: #303133; }
.mde-tb-btn:active { background: #e4e7ed; }
.mde-tb-sample { width: auto; padding: 0 10px; font-size: 12px; font-weight: 600; color: #4e7cff; }
.mde-tb-right { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.mde-stats { font-size: 11px; color: #b0b8c4; white-space: nowrap; }

/* panel = editor + preview as one card */
.mde-panel {
  border: 1px solid #dcdfe6; border-radius: 0 0 8px 8px; overflow: hidden;
  display: flex; flex-direction: column;
}
.mde-section { }
.mde-section-hd {
  padding: 8px 14px; font-size: 11px; font-weight: 600; color: #b0b8c4;
  text-transform: uppercase; letter-spacing: 1px; background: #fafbfc;
  border-bottom: 1px solid #ebeef5;
}
.mde-section-divider { height: 1px; background: #e4e7ed; }

.mde-ta {
  display: block; width: 100%; min-height: 280px; border: none; outline: none; resize: vertical;
  padding: 16px; font-size: 14px; font-family: 'SF Mono','Fira Code',Consolas,monospace;
  line-height: 1.7; color: #303133; background: #fff;
}
.mde-ta:focus { background: #fcfcfd; }
.mde-ta::placeholder { color: #c0c4cc; }

.mde-preview { padding: 16px; min-height: 120px; }

/* empty state */
.mde-section-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 40px; background: #fafbfc; min-height: 200px;
}
.mde-empty-icon { font-size: 40px; color: #dcdfe6; margin-bottom: 12px; }
.mde-empty-title { font-size: 15px; font-weight: 600; color: #909399; margin-bottom: 8px; }
.mde-empty-text { font-size: 13px; color: #c0c4cc; margin-bottom: 20px; }
.mde-empty-hint { display: flex; flex-direction: column; gap: 6px; }
.mde-empty-hint div { font-size: 12px; color: #c0c4cc; font-family: 'SF Mono',monospace; }
.mde-quick-ref { color: #b0b8cc; font-weight: 600; margin-bottom: 10px; }

/* rendered markdown */
.mde-c h1 { font-size: 1.6em; border-bottom: 2px solid #e4e7ed; padding-bottom: 8px; margin: 0 0 14px; color: #1a1f36; }
.mde-c h2 { font-size: 1.3em; margin: 20px 0 10px; color: #1a1f36; }
.mde-c h3 { font-size: 1.1em; margin: 16px 0 8px; color: #303133; }
.mde-c h4 { font-size: 1em; margin: 12px 0 6px; color: #303133; }
.mde-c p { margin: 0 0 10px; line-height: 1.8; }
.mde-c ul, .mde-c ol { padding-left: 24px; margin: 0 0 10px; }
.mde-c li { margin-bottom: 4px; line-height: 1.7; }
.mde-c code { background: #f0f2f5; padding: 2px 6px; border-radius: 4px; font-family: 'SF Mono','Fira Code',monospace; font-size: .88em; color: #d63384; }
.mde-c pre { background: #1e1e2e; color: #cdd6f4; padding: 14px 16px; border-radius: 8px; overflow-x: auto; margin: 0 0 12px; line-height: 1.6; }
.mde-c pre code { background: none; padding: 0; color: inherit; font-size: 13px; }
.mde-c blockquote { border-left: 3px solid #4e7cff; color: #606266; padding: 8px 16px; margin: 0 0 12px; background: #f5f7ff; border-radius: 0 6px 6px 0; }
.mde-c blockquote p { margin: 0; }
.mde-c a { color: #4e7cff; text-decoration: none; }
.mde-c a:hover { text-decoration: underline; }
.mde-c hr { border: none; border-top: 1px solid #e4e7ed; margin: 18px 0; }
.mde-c img { max-width: 100%; border-radius: 6px; display: block; }
.mde-c strong { color: #1a1f36; }
.mde-c del { color: #909399; }
.mde-c table { width: 100%; border-collapse: collapse; margin: 0 0 14px; font-size: 13px; }
.mde-c th, .mde-c td { padding: 8px 12px; border: 1px solid #e4e7ed; text-align: left; }
.mde-c th { background: #f5f7fa; font-weight: 600; color: #303133; }
.mde-c td { color: #4a5568; }
.mde-c .tasks { list-style: none; padding-left: 4px; }
.mde-c .tasks li { display: flex; align-items: flex-start; gap: 8px; }
.mde-c .tasks input[type=checkbox] { margin-top: 4px; accent-color: #4e7cff; }
.mde-c .tasks .checked span { text-decoration: line-through; color: #909399; }

@media (max-width: 768px) {
  .mde-tb-sep { display: none; }
  .mde-stats { display: none; }
  .mde-ta { min-height: 240px; }
}
</style>
